import {HDate, Locale} from '@hebcal/hdate';
import {CalOptions} from './CalOptions.js';
import {Location} from './location.js';
import {Event} from './event.js';
import {reformatTimeStr} from './reformatTimeStr.js';
import {Zmanim} from './zmanim.js';
import {holidayDesc as hdesc} from './staticHolidays.js';
import './locale.js'; // Adds Hebrew and Ashkenazic translations

const {HAVDALAH, CANDLE_LIGHTING} = hdesc;

/**
 * Base class for events that have an associated wall-clock time and
 * geographic location — for example, candle lighting, havdalah, and
 * fast begin/end times.
 *
 * Stores both a `Date` (`eventTime`) and pre-formatted 24-hour
 * (`eventTimeStr`) and locale-aware (`fmtTime`) strings. May reference a
 * "linked" event such as the holiday whose candle-lighting time this
 * represents.
 */
export class TimedEvent extends Event {
  /** The exact moment of the event, rounded to the nearest minute */
  readonly eventTime: Date;
  /** Geographic location used to compute `eventTime` */
  readonly location: Location;
  /** 24-hour formatted time string in the location's timezone (e.g. `"19:42"`) */
  readonly eventTimeStr: string;
  /** Locale-aware formatted time string (e.g. `"7:42pm"` in US locale) */
  readonly fmtTime: string;
  /** Optional event this time is associated with (e.g. the Yom Tov being lit for) */
  readonly linkedEvent?: Event;
  /**
   * Optional length of the event in minutes. Not computed or validated by
   * this class; it's a plain count of minutes for downstream renderers
   * (e.g. `@hebcal/icalendar`) to add to the event's start wall-clock time
   * when producing a non-zero-duration `DTEND`. Undefined means no
   * duration was specified.
   */
  duration?: number;
  /**
   * Normally created by {@link calendar} rather than directly.
   * @param date Hebrew date the event occurs
   * @param desc Description (not translated)
   * @param mask optional holiday flags
   * @param eventTime the exact moment of the event
   * @param location geographic location used to format the time
   * @param linkedEvent optional event this time is associated with
   * @param options optional; `locale` and `hour12` affect {@link fmtTime}
   */
  constructor(
    date: HDate,
    desc: string,
    mask: number,
    eventTime: Date,
    location: Location,
    linkedEvent?: Event,
    options?: CalOptions
  ) {
    super(date, desc, mask);
    this.eventTime = Zmanim.roundTime(eventTime);
    this.location = location;
    const timeFormat = location.getTimeFormatter();
    this.eventTimeStr = Zmanim.formatTime(this.eventTime, timeFormat);
    const opts = {...options, location};
    this.fmtTime = reformatTimeStr(this.eventTimeStr, 'pm', opts);
    if (linkedEvent !== undefined) {
      this.linkedEvent = linkedEvent;
    }
  }
  /**
   * @param [locale] Optional locale name (defaults to empty locale)
   */
  render(locale?: string): string {
    return `${Locale.gettext(this.getDesc(), locale)}: ${this.fmtTime}`;
  }
  /**
   * Returns translation of "Candle lighting" without the time.
   * @param [locale] Optional locale name (defaults to empty locale)
   */
  renderBrief(locale?: string): string {
    return Locale.gettext(this.getDesc(), locale);
  }

  getCategories(): string[] {
    const desc = this.getDesc();
    switch (desc) {
      // LIGHT_CANDLES or LIGHT_CANDLES_TZEIS
      case CANDLE_LIGHTING:
        return ['candles'];
      // YOM_TOV_ENDS
      case HAVDALAH:
        return ['havdalah'];
      // flags.MINOR_FAST or flags.MAJOR_FAST
      case hdesc.FAST_BEGINS:
      case hdesc.FAST_ENDS:
        return ['zmanim', 'fast'];
      case hdesc.SOF_ZMAN_ACHILAT_CHAMETZ:
        return ['zmanim', 'achilasChametz'];
      case hdesc.BIUR_CHAMETZ:
        return ['zmanim', 'biurChametz'];
    }
    /* NOTREACHED */
    return ['unknown'];
  }
}

/**
 * Candle-lighting event for the eve of Shabbat or a Yom Tov.
 *
 * Generated by {@link calendar} when `options.candlelighting`
 * is `true` and `options.location` is provided. By default candle lighting
 * occurs 18 minutes before sundown in the Diaspora and 20 minutes before
 * sundown in Israel (40 minutes in Jerusalem; 30 minutes in Haifa and
 * Zikhron Ya'akov), configurable via `options.candleLightingMins`.
 */
export class CandleLightingEvent extends TimedEvent {
  constructor(
    date: HDate,
    mask: number,
    eventTime: Date,
    location: Location,
    linkedEvent?: Event,
    options?: CalOptions
  ) {
    super(date, CANDLE_LIGHTING, mask, eventTime, location, linkedEvent, options);
  }

  getEmoji(): string {
    return '🕯️';
  }
}

/**
 * Havdalah event marking the end of Shabbat or a Yom Tov.
 *
 * Generated by {@link calendar} when `options.candlelighting`
 * is `true` and `options.location` is provided. By default Havdalah is
 * calculated by *tzeit hakochavim* (8.5° solar depression); pass
 * `options.havdalahMins` to use a fixed minute offset instead, or
 * `options.havdalahDeg` to use a different degree.
 */
export class HavdalahEvent extends TimedEvent {
  private readonly havdalahMins?: number;
  constructor(
    date: HDate,
    mask: number,
    eventTime: Date,
    location: Location,
    havdalahMins?: number,
    linkedEvent?: Event,
    options?: CalOptions
  ) {
    super(date, HAVDALAH, mask, eventTime, location, linkedEvent, options);
    if (havdalahMins) {
      this.havdalahMins = havdalahMins;
    }
  }
  /**
   * @param [locale] Optional locale name (defaults to empty locale)
   */
  render(locale?: string): string {
    return `${this.renderBrief(locale)}: ${this.fmtTime}`;
  }
  /**
   * Returns translation of "Havdalah" without the time.
   * @param [locale] Optional locale name (defaults to empty locale)
   */
  renderBrief(locale?: string): string {
    let str = Locale.gettext(this.getDesc(), locale);
    if (this.havdalahMins) {
      const min = Locale.gettext('min', locale);
      str += ` (${this.havdalahMins} ${min})`;
    }
    return str;
  }

  getEmoji(): string {
    return '✨';
  }
}
